import { useEffect, useState } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { SingleUploader } from '../../components/MediaUploader';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { useSite } from '../../context/SiteContext';
import { CONTENT_GROUPS } from '../../lib/siteContent';

const EMPTY = {
  company_name: 'Terra Homes Realty', tagline: 'Your Home, Our Goal',
  address: '', phone: '', mobile: '', email: '',
  logo: '/assets/logo.png', hero_image: '', hero_title: '', hero_subtitle: '',
  facebook: '', instagram: '', twitter: '', linkedin: '',
  about_story: '', content: {}
};

export default function AdminSettings() {
  const { refresh } = useSite();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/admin/settings').then(r => setForm({ ...EMPTY, ...r.data, content: r.data.content || {} })); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setContent = (k, v) => setForm(f => ({ ...f, content: { ...f.content, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/admin/settings', form);
      toast({ title: 'Settings saved' });
      refresh();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold text-neutral-900 mb-1">Site Settings</h1>
      <p className="text-sm text-neutral-500 mb-6">Manage your company information, logo, and content shown across the site.</p>
      <form onSubmit={submit} className="space-y-5">
        <div className="bg-white p-6 rounded-xl border border-neutral-200">
          <h2 className="font-bold text-neutral-900 mb-4">Branding</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <SingleUploader value={form.logo?.startsWith('/api/') ? form.logo : ''} onChange={(v) => set('logo', v || '/assets/logo.png')} label="Company Logo (upload to replace default)" />
            <SingleUploader value={form.hero_image} onChange={(v) => set('hero_image', v)} label="Homepage Hero Image" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Company Name</label><Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Tagline</label><Input value={form.tagline} onChange={(e) => set('tagline', e.target.value)} /></div>
            <div className="md:col-span-2"><label className="text-sm font-medium text-neutral-700 mb-1 block">Hero Title</label><Input value={form.hero_title} onChange={(e) => set('hero_title', e.target.value)} /></div>
            <div className="md:col-span-2"><label className="text-sm font-medium text-neutral-700 mb-1 block">Hero Subtitle</label><Textarea rows={2} value={form.hero_subtitle} onChange={(e) => set('hero_subtitle', e.target.value)} /></div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-neutral-200">
          <h2 className="font-bold text-neutral-900 mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className="text-sm font-medium text-neutral-700 mb-1 block">Office Address</label><Input value={form.address} onChange={(e) => set('address', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Landline</label><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(032) 555-0888" /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Mobile</label><Input value={form.mobile} onChange={(e) => set('mobile', e.target.value)} placeholder="+63 917 815 0888" /></div>
            <div className="md:col-span-2"><label className="text-sm font-medium text-neutral-700 mb-1 block">Email</label><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-neutral-200">
          <h2 className="font-bold text-neutral-900 mb-4">Social Media</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Facebook</label><Input value={form.facebook} onChange={(e) => set('facebook', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Instagram</label><Input value={form.instagram} onChange={(e) => set('instagram', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Twitter</label><Input value={form.twitter} onChange={(e) => set('twitter', e.target.value)} /></div>
            <div><label className="text-sm font-medium text-neutral-700 mb-1 block">LinkedIn</label><Input value={form.linkedin} onChange={(e) => set('linkedin', e.target.value)} /></div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-neutral-200">
          <h2 className="font-bold text-neutral-900 mb-4">About Page</h2>
          <label className="text-sm font-medium text-neutral-700 mb-1 block">Company Story</label>
          <Textarea rows={6} value={form.about_story} onChange={(e) => set('about_story', e.target.value)} />
        </div>

        <div className="bg-white p-6 rounded-xl border border-neutral-200">
          <h2 className="font-bold text-neutral-900 mb-1">Page Content</h2>
          <p className="text-sm text-neutral-500 mb-5">Edit the headings and write-ups shown across the public pages. Leave a field blank to use the default text.</p>
          <div className="space-y-6">
            {CONTENT_GROUPS.map((g) => (
              <div key={g.group}>
                <h3 className="text-sm font-bold text-[#DC2626] uppercase tracking-wider mb-3">{g.group}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {g.fields.map((f) => (
                    <div key={f.key} className={f.multiline ? 'md:col-span-2' : ''}>
                      <label className="text-sm font-medium text-neutral-700 mb-1 block">{f.label}</label>
                      {f.multiline ? (
                        <Textarea rows={2} value={form.content?.[f.key] ?? ''} onChange={(e) => setContent(f.key, e.target.value)} placeholder={f.default} />
                      ) : (
                        <Input value={form.content?.[f.key] ?? ''} onChange={(e) => setContent(f.key, e.target.value)} placeholder={f.default} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button type="submit" disabled={saving} className="btn-primary gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings</Button>
        </div>
      </form>
    </div>
  );
}
